import { handleError } from './copilot-client';
import { RequestTimeoutError } from './error';

export function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export type AffineTextEvent = {
  type:
    | 'attachment'
    | 'message'
    | 'reasoning'
    | 'tool-call-start'
    | 'tool-call'
    | 'tool-result'
    | 'tool-execution-started'
    | 'usage'
    | 'meta'
    | 'warning'
    | 'routing'; // sl-sf9o: model routing telemetry
  data: string;
};

type AffineTextStream = AsyncIterable<AffineTextEvent>;

type toTextStreamOptions = {
  timeout?: number;
  signal?: AbortSignal;
};

const safeParseError = (
  data: string
): { status?: number; code?: string; message?: string; keySource?: string } => {
  try {
    return JSON.parse(data);
  } catch {
    return {
      status: 500,
    };
  }
};

export function toTextStream(
  eventSource: EventSource,
  { timeout, signal }: toTextStreamOptions = {}
): AffineTextStream {
  return {
    [Symbol.asyncIterator]: async function* () {
      const messageQueue: AffineTextEvent[] = [];
      let resolveMessagePromise: () => void;
      let finished = false;
      let streamError: Error | undefined;
      function resetMessagePromise() {
        resolveMessagePromise?.();
        return new Promise<void>(resolve => {
          resolveMessagePromise = resolve;
        });
      }
      let messagePromise = resetMessagePromise();
      function messageListener(event: MessageEvent) {
        if (finished || signal?.aborted) return;
        messageQueue.push({
          type: event.type as AffineTextEvent['type'],
          data: event.data as string,
        });
        messagePromise = resetMessagePromise();
      }
      function errorListener(event: Event) {
        const errorMessage = (event as MessageEvent<string>).data;
        if (errorMessage)
          streamError = handleError(safeParseError(errorMessage));
        finished = true;
        resolveMessagePromise();
      }
      function abortListener() {
        resolveMessagePromise();
      }
      const events = [
        'message',
        'attachment',
        'reasoning',
        'tool-call-start',
        'tool-call',
        'tool-result',
        'tool-execution-started',
        'usage',
        'meta',
        'warning',
        'routing',
      ];
      for (const type of events)
        eventSource.addEventListener(type, messageListener);
      eventSource.addEventListener('error', errorListener);
      signal?.addEventListener('abort', abortListener, { once: true });
      try {
        // Events that arrived before the stream ended are still handed
        // over, so a reader slower than the server loses nothing; a stop
        // drops them.
        while (
          !signal?.aborted &&
          (messageQueue.length > 0 ||
            (!finished && eventSource.readyState !== EventSource.CLOSED))
        ) {
          if (!messageQueue.length) {
            let timer: ReturnType<typeof setTimeout> | undefined;
            try {
              await (timeout
                ? Promise.race([
                    messagePromise,
                    new Promise<void>((_, reject) => {
                      timer = setTimeout(
                        () => reject(new RequestTimeoutError()),
                        timeout
                      );
                    }),
                  ])
                : messagePromise);
            } finally {
              if (timer !== undefined) clearTimeout(timer);
            }
          } else {
            const top = messageQueue.shift();
            if (top) yield top;
          }
        }
        if (!signal?.aborted && streamError) throw streamError;
      } finally {
        for (const type of events)
          eventSource.removeEventListener(type, messageListener);
        eventSource.removeEventListener('error', errorListener);
        signal?.removeEventListener('abort', abortListener);
        if (eventSource.readyState !== EventSource.CLOSED) eventSource.close();
      }
    },
  };
}
